/**
 * Tema (Light / Dark) boshqaruvi.
 *
 * Uch rejim:
 *   'auto'  — Telegram colorScheme'ga, u yo'q bo'lsa OS `prefers-color-scheme`ga ergashadi
 *   'light' — majburiy yorug'
 *   'dark'  — majburiy to'q
 *
 * Foydalanuvchi tanlovi 'auto' dan farq qilsa, u USTUN turadi: Telegram dark'da
 * ochilsa ham 'light' tanlangan bo'lsa yorug' qoladi (va aksincha).
 *
 * Yangi npm paket QO'SHILMAGAN (CLAUDE.md §3.4) — mavjud zustand ishlatiladi.
 * Haqiqiy ranglar `src/index.css` dagi CSS o'zgaruvchilarda; bu modul faqat
 * <html data-theme="..."> atributini boshqaradi.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { THEME_STORAGE_KEY } from '@/lib/constants';

export type ThemeMode = 'auto' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

/** index.css dagi `--color-chrome` bilan bir xil bo'lishi SHART (index.html anti-flash skripti ham shunga tayanadi). */
const CHROME_COLOR: Record<ResolvedTheme, string> = {
  light: '#EFEFF4',
  dark: '#0E1621',
};

function getTelegramColorScheme(): ResolvedTheme | null {
  const tg = window.Telegram?.WebApp;
  if (!tg) return null;
  // Telegram 6.0 dan pastda colorScheme bo'lmasligi mumkin
  return tg.colorScheme === 'dark' ? 'dark' : tg.colorScheme === 'light' ? 'light' : null;
}

function getSystemColorScheme(): ResolvedTheme {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** 'auto' rejimida haqiqiy temani aniqlaydi: Telegram > OS. */
export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'light' || mode === 'dark') return mode;
  return getTelegramColorScheme() ?? getSystemColorScheme();
}

/**
 * <html> ga temani qo'llaydi va brauzer/Telegram chrome ranglarini moslaydi.
 * `animate` — foydalanuvchi qo'lda almashtirganda yumshoq o'tish; dastlabki
 * yuklanishda va tashqi (OS/Telegram) o'zgarishda kerak emas.
 */
export function applyTheme(theme: ResolvedTheme, animate = false): void {
  const root = document.documentElement;

  if (animate) {
    root.classList.add('theme-transition');
    window.setTimeout(() => root.classList.remove('theme-transition'), 260);
  }

  root.dataset.theme = theme;

  const chrome = CHROME_COLOR[theme];

  // Brauzer manzil paneli / PWA status bar
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', chrome);

  // Telegram Mini App sarlavha va fon rangi
  const tg = window.Telegram?.WebApp;
  if (tg) {
    try {
      tg.setHeaderColor(chrome);
      tg.setBackgroundColor(chrome);
    } catch {
      // Telegram'ning eski versiyalari setHeaderColor'ni qo'llab-quvvatlamaydi — muhim emas
    }
  }
}

interface ThemeState {
  /** Foydalanuvchi tanlovi (saqlanadi) */
  mode: ThemeMode;
  /** Ekranda ko'rinayotgan haqiqiy tema */
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
  /** Tashqi manba (OS / Telegram) o'zgarganda ichki holatni yangilash */
  syncFromEnvironment: () => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'auto',
      resolved: 'light',

      setMode: (mode) => {
        const resolved = resolveTheme(mode);
        applyTheme(resolved, resolved !== get().resolved);
        set({ mode, resolved });
      },

      syncFromEnvironment: () => {
        const { mode, resolved } = get();
        if (mode !== 'auto') return; // qo'lda tanlov ustun
        const next = resolveTheme('auto');
        if (next === resolved) return;
        applyTheme(next);
        set({ resolved: next });
      },
    }),
    {
      name: THEME_STORAGE_KEY,
      // `resolved` hosila qiymat — saqlanmaydi, har safar qaytadan hisoblanadi
      partialize: (s) => ({ mode: s.mode }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const resolved = resolveTheme(state.mode);
        applyTheme(resolved);
        state.resolved = resolved;
      },
    },
  ),
);

/**
 * `main.tsx` da bir marta chaqiriladi (React render'dan oldin).
 * OS va Telegram tema o'zgarishlariga obuna bo'ladi.
 */
export function initTheme(): void {
  const store = useThemeStore.getState();
  const resolved = resolveTheme(store.mode);
  applyTheme(resolved);
  useThemeStore.setState({ resolved });

  const sync = (): void => useThemeStore.getState().syncFromEnvironment();

  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener('change', sync);
  window.Telegram?.WebApp?.onEvent('themeChanged', sync);
}

/** Komponentlarda qulay hook. */
export function useTheme(): {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
  isDark: boolean;
} {
  const mode = useThemeStore((s) => s.mode);
  const resolved = useThemeStore((s) => s.resolved);
  const setMode = useThemeStore((s) => s.setMode);
  return { mode, resolved, setMode, isDark: resolved === 'dark' };
}
